import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { supabase } from '@/lib/supabase-client';
import { getActiveTenant, isSubscriptionActive } from '@/lib/tenant';
import type { EmailOtpType } from '@supabase/supabase-js';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/auth/callback')({
    component: AuthCallback,
});

type Status = 'pending' | 'success' | 'error';

function AuthCallback() {
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('pending');
    const [message, setMessage] = useState('Confirming your email...');

    useEffect(() => {
        let isMounted = true;

        const run = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const hash = new URLSearchParams(
                    window.location.hash.replace(/^#/, ''),
                );
                const tokenHash = params.get('token_hash');
                const type = params.get('type');
                const code = params.get('code');
                const accessToken = hash.get('access_token');
                const refreshToken = hash.get('refresh_token');
                const hashError = hash.get('error_description');
                const hashErrorCode = hash.get('error_code');
                const queryError = params.get('error_description');
                const queryErrorCode = params.get('error_code');

                if (hashError || queryError) {
                    const rawError = decodeURIComponent(
                        hashError ?? queryError ?? '',
                    );
                    const errorCode = hashErrorCode ?? queryErrorCode ?? '';
                    if (
                        errorCode === 'otp_expired' ||
                        rawError.toLowerCase().includes('expired')
                    ) {
                        throw new Error(
                            'This confirmation link has expired. Please request a new one.',
                        );
                    }
                    throw new Error(rawError || 'Unable to confirm your email.');
                }

                if (accessToken && refreshToken) {
                    const { error } = await supabase.auth.setSession({
                        access_token: accessToken,
                        refresh_token: refreshToken,
                    });
                    if (error) {
                        throw new Error(error.message);
                    }
                } else if (tokenHash && type) {
                    const { error } = await supabase.auth.verifyOtp({
                        token_hash: tokenHash,
                        type: type as EmailOtpType,
                    });
                    if (error) {
                        throw new Error(error.message);
                    }
                } else if (code) {
                    const { error } =
                        await supabase.auth.exchangeCodeForSession(code);
                    if (error) {
                        throw new Error(error.message);
                    }
                } else {
                    throw new Error('Missing confirmation details.');
                }

                const {
                    data: { session },
                } = await supabase.auth.getSession();

                if (session) {
                    const tenant = await getActiveTenant();
                    navigate({
                        to: isSubscriptionActive(tenant.subscriptionStatus)
                            ? '/dashboard'
                            : '/subscribe',
                        replace: true,
                    });
                    return;
                }

                if (isMounted) {
                    setStatus('success');
                    setMessage('Email confirmed. You can sign in now.');
                }
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                setStatus('error');
                setMessage(
                    error instanceof Error
                        ? error.message
                        : 'Unable to confirm your email.',
                );
            }
        };

        void run();

        return () => {
            isMounted = false;
        };
    }, [navigate]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>Email confirmation</CardTitle>
                    <CardDescription>
                        {status === 'pending'
                            ? 'Hang tight while we verify your email.'
                            : 'You can return to sign in once this is done.'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="rounded-md border border-border bg-card/60 px-3 py-2 text-sm text-muted-foreground">
                        {message}
                    </div>
                    {status !== 'pending' && (
                        <Button asChild className="w-full">
                            <Link to="/auth">Back to sign in</Link>
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
